import { activeRegionAtom } from "@/atoms/activeRegion";
import { unitSystemAtom } from "@/atoms/units";
import {
  findRegionInZones,
  regionHasCameras,
  useRegions,
  useZones,
} from "@/hooks/useRegions";
import { trackRegionChange, trackSettingsUnitChange } from "@/lib/analytics";
import { activeCameraIdAtom } from "@/atoms/activeCamera";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MapIcon from "@mui/icons-material/Map";
import PlaceIcon from "@mui/icons-material/Place";
import PublicIcon from "@mui/icons-material/Public";
import VideocamIcon from "@mui/icons-material/Videocam";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";

/** Map first zone ID in each geographic group → section label */
const SECTION_STARTS: Record<string, string> = {
  "us-east-coast": "Americas",
  "western-europe": "Europe",
  "atlantic-islands": "Atlantic & Africa",
  indonesia: "Asia",
  australia: "Oceania",
};

function sectionStart(zoneId: string): string | null {
  return SECTION_STARTS[zoneId] ?? null;
}

interface NavigationDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function NavigationDrawer({ open, onClose }: NavigationDrawerProps) {
  const [activeRegion, setActiveRegion] = useRecoilState(activeRegionAtom);
  const [unitSystem, setUnitSystem] = useRecoilState(unitSystemAtom);
  const setSavedCameraId = useSetRecoilState(activeCameraIdAtom);
  const navigate = useNavigate();
  const { data: zones, isLoading: zonesLoading } = useZones();
  const { data: regionsMap, isLoading: regionsLoading } = useRegions();
  const [expandedZones, setExpandedZones] = useState<Set<string>>(() => {
    // Auto-expand the zone containing the active region
    const found = findRegionInZones(zones ?? [], activeRegion);
    return new Set(found ? [found.zone.id] : [(zones ?? [])[0]?.id]);
  });
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(() => {
    const found = findRegionInZones(zones ?? [], activeRegion);
    return new Set(found ? [found.country.id] : []);
  });

  const toggleZone = (zoneId: string) => {
    setExpandedZones((prev) => {
      const next = new Set(prev);
      if (next.has(zoneId)) next.delete(zoneId);
      else next.add(zoneId);
      return next;
    });
  };

  const toggleCountry = (countryId: string) => {
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      if (next.has(countryId)) next.delete(countryId);
      else next.add(countryId);
      return next;
    });
  };

  const handleRegionClick = (regionId: string) => {
    setActiveRegion(regionId);
    setSavedCameraId(null);
    trackRegionChange(regionId);
    navigate(`/spot/${encodeURIComponent(regionId)}`, { replace: true });
    onClose();
  };

  const handleMapClick = () => {
    navigate("/surf-spots");
    onClose();
  };

  const renderRegions = (regions: { id: string; label: string }[], pl: number) =>
    regions.map((region) => {
      const isActive = activeRegion === region.id;
      const regionData = regionsMap?.[region.id];
      const hasCams = regionData ? regionHasCameras(regionData) : false;
      const firstBreak = regionData?.breaks?.[0];
      const hasLocation = !!(firstBreak?.lat && firstBreak?.lon);

      return (
        <ListItemButton
          key={region.id}
          selected={isActive}
          onClick={() => handleRegionClick(region.id)}
          sx={{
            py: 0.4,
            pl,
            pr: 2,
            borderLeft: "2px solid",
            borderColor: isActive ? "accent.main" : "transparent",
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: "action.hover" },
            "&.Mui-selected": { backgroundColor: "action.selected" },
          }}
        >
          <ListItemText
            primary={region.label}
            primaryTypographyProps={{
              fontSize: "0.8125rem",
              fontWeight: isActive ? 500 : 400,
              color: isActive ? "accent.main" : "ink.main",
            }}
          />
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            {hasCams && (
              <VideocamIcon sx={{ fontSize: 12, color: "accent.main", opacity: 0.7 }} />
            )}
            {hasLocation && (
              <PlaceIcon sx={{ fontSize: 12, color: "kelp.main", opacity: 0.7 }} />
            )}
          </Box>
        </ListItemButton>
      );
    });

  if (zonesLoading || regionsLoading) return null;

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: 280, sm: 320 },
          backgroundColor: "background.default",
          borderRight: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="overline" sx={{ color: "ink.muted" }}>
          Surf Regions
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "ink.muted" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tree */}
      <List dense sx={{ pt: 0.5, overflow: "auto", flex: 1 }}>
        <ListItemButton
          onClick={handleMapClick}
          sx={{
            py: 0.75,
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: "accent.main" }}>
            <MapIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primary="Surf spots map"
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "ink.main",
            }}
          />
        </ListItemButton>
        {(zones ?? []).map((zone, zoneIdx) => (
          <Box key={zone.id}>
            {/* Section headers based on zone grouping */}
            {sectionStart(zone.id) && (
              <>
                {zoneIdx > 0 && <Divider sx={{ my: 0.5 }} />}
                <Typography
                  variant="overline"
                  component="div"
                  sx={{
                    color: "ink.muted",
                    px: 2,
                    pt: zoneIdx === 0 ? 0.5 : 1,
                    pb: 0.25,
                  }}
                >
                  {sectionStart(zone.id)}
                </Typography>
              </>
            )}
            {/* Zone header */}
            <ListItemButton
              onClick={() => toggleZone(zone.id)}
              sx={{
                py: 0.75,
                px: 2,
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: "ink.muted" }}>
                <PublicIcon sx={{ fontSize: 16 }} />
              </ListItemIcon>
              <ListItemText
                primary={zone.label}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "ink.main",
                }}
              />
              {expandedZones.has(zone.id) ? (
                <ExpandMoreIcon sx={{ fontSize: 16, color: "ink.muted" }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: 16, color: "ink.muted" }} />
              )}
            </ListItemButton>

            <Collapse in={expandedZones.has(zone.id)}>
              {zone.countries.length === 1
                ? /* Single-country zones: flatten — show regions directly under zone */
                  renderRegions(zone.countries[0].regions, 4)
                : zone.countries.map((country) => (
                    <Box key={country.id}>
                      <ListItemButton
                        onClick={() => toggleCountry(country.id)}
                        sx={{
                          py: 0.5,
                          pl: 4,
                          pr: 2,
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <ListItemText
                          primary={country.label}
                          primaryTypographyProps={{
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            color: "ink.main",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "ink.muted",
                            fontSize: "0.6875rem",
                            mr: 0.5,
                          }}
                        >
                          {country.regions.length}
                        </Typography>
                        {expandedCountries.has(country.id) ? (
                          <ExpandMoreIcon sx={{ fontSize: 14, color: "ink.muted" }} />
                        ) : (
                          <ChevronRightIcon sx={{ fontSize: 14, color: "ink.muted" }} />
                        )}
                      </ListItemButton>

                      <Collapse in={expandedCountries.has(country.id)}>
                        {renderRegions(country.regions, 6)}
                      </Collapse>
                    </Box>
                  ))}
            </Collapse>
          </Box>
        ))}
      </List>

      {/* Units — mobile only. Desktop keeps the toggle in the toolbar. */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="overline" sx={{ color: "ink.muted" }}>
          Units
        </Typography>
        <ToggleButtonGroup
          value={unitSystem}
          exclusive
          onChange={(_, v) => {
            if (v) {
              setUnitSystem(v);
              trackSettingsUnitChange(v);
            }
          }}
          size="small"
          sx={{
            height: 28,
            "& .MuiToggleButton-root": {
              fontSize: "0.65rem",
              fontWeight: 600,
              px: 1.25,
              py: 0,
              color: "text.secondary",
              borderColor: "divider",
              "&.Mui-selected": {
                color: "accent.main",
                backgroundColor: "transparent",
              },
            },
          }}
        >
          <ToggleButton value="metric">°C / m</ToggleButton>
          <ToggleButton value="imperial">°F / ft</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <VideocamIcon sx={{ fontSize: 11, color: "accent.main", opacity: 0.7 }} />
          <Typography variant="overline" sx={{ color: "ink.muted" }}>
            Camera
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <PlaceIcon sx={{ fontSize: 11, color: "kelp.main", opacity: 0.7 }} />
          <Typography variant="overline" sx={{ color: "ink.muted" }}>
            Forecast
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}
